import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box, Stack, Typography, TextField, MenuItem, Button, Alert, Paper,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  IconButton, Tooltip, CircularProgress,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { BRAND } from "../styles/theme";
import { Collector, listCollectors } from "../services/collectors";
import { ProcessorChip, BoolChip } from "../components/collectors/chips";

type StatusFilter = "all" | "active" | "inactive";

/**
 * Picker de "Collector Operation" (v1: `MenuCollectorOperation`) — reemplaza los
 * 3 toggles progresivos All/Active/Settlement por select + estado en la URL,
 * mismo patrón que `Collectors.tsx`. A diferencia de esa pantalla, acá el click
 * de fila navega al ledger de movimientos (`/collector-operation/:id`), no al
 * detalle de gestión (`/collector/:id`) — son dos entry points distintos sobre
 * el mismo listado de collectors.
 */
export default function CollectorOperation() {
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  const q      = sp.get("q") ?? "";
  const status = (sp.get("status") as StatusFilter) || "all";
  const setParam = (k: string, v: string) => {
    const next = new URLSearchParams(sp);
    if (v) next.set(k, v); else next.delete(k);
    setSp(next, { replace: true });
  };

  const [rows, setRows]       = useState<Collector[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr]         = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true); setErr(null);
    listCollectors()
      .then((r) => { if (alive) setRows(r); })
      .catch((e) => { if (alive) setErr(String(e?.message ?? e)); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return rows
      .filter((c) => {
        if (status === "active" && !c.active) return false;
        if (status === "inactive" && c.active) return false;
        if (!s) return true;
        return [c.id, c.paymentsProcessor].some((v) => v.toLowerCase().includes(s));
      })
      .sort((a, b) => a.id.localeCompare(b.id, undefined, { sensitivity: "base" }));
  }, [rows, q, status]);

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>Movimientos de collector</Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
        Pick a collector to view its settlement movements, add a manual movement, or change a transfer's status.
      </Typography>

      <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} alignItems={{ md: "center" }} sx={{ mb: 2 }}>
        <TextField size="small" label="Search" placeholder="Id, processor…" value={q}
          onChange={(e) => setParam("q", e.target.value)} sx={{ width: 320 }} />
        <TextField size="small" select label="Status" value={status}
          onChange={(e) => setParam("status", e.target.value === "all" ? "" : e.target.value)} sx={{ width: 160 }}>
          <MenuItem value="all">All</MenuItem>
          <MenuItem value="active">Active</MenuItem>
          <MenuItem value="inactive">Inactive</MenuItem>
        </TextField>
      </Stack>

      {err && <Alert severity="error" onClose={() => setErr(null)} sx={{ mb: 2 }}>{err}</Alert>}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead sx={{ bgcolor: BRAND.primaryLight }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Id</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Processor</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Close</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Settlement acct</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4 }}><CircularProgress size={24} /></TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={6} align="center" sx={{ py: 4, color: "text.secondary" }}>
                {rows.length === 0 ? "No collectors." : <>No collectors match. <Button size="small" onClick={() => setSp({}, { replace: true })}>Clear filters</Button></>}
              </TableCell></TableRow>
            ) : filtered.map((c) => (
              <TableRow key={c.id} hover sx={{ cursor: "pointer", ...(c.active ? {} : { "& td": { opacity: 0.55 } }) }}
                onClick={() => nav(`/collector-operation/${encodeURIComponent(c.id)}`)}>
                <TableCell sx={{ fontWeight: 700 }}>{c.id}</TableCell>
                <TableCell><ProcessorChip value={c.paymentsProcessor} /></TableCell>
                <TableCell sx={{ fontFamily: "monospace" }}>{c.closeId || <span style={{ color: "#888" }}>—</span>}</TableCell>
                <TableCell><BoolChip value={c.active} yes="Active" no="Inactive" /></TableCell>
                <TableCell><BoolChip value={c.settlementEnabled} yes="Enabled" no="Disabled" /></TableCell>
                <TableCell align="right" sx={{ opacity: 1 }}>
                  <Tooltip title="Open"><IconButton size="small"><ChevronRightIcon /></IconButton></Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <Stack direction="row" alignItems="center" sx={{ px: 2, py: 1, borderTop: 1, borderColor: "divider" }}>
          <Typography variant="caption" color="text.secondary">{filtered.length} of {rows.length} collectors</Typography>
        </Stack>
      </TableContainer>
    </Box>
  );
}
